/**
 * ProzessErinnerungenPage — Administration › Prozess-Erinnerungen (Berechtigungskonzept V4.0 · P7,
 * per-process rework — Stefan point 7).
 *
 * Per PROCESS TYPE, three escalation levels:
 *   Stufe 1 + 2 = kundengerichtete Erinnerung. Der Kanal folgt einer Regel (kein Toggle mehr): hat der
 *                 Kunde aktiven Kundenportal-Zugang, landet die Erinnerung im Portal, sonst per E-Mail.
 *   Stufe 3     = automatisch eine Aufgabe beim Kundenberater erstellen (Kunde kontaktieren).
 * Die Fristen sind pro Prozess einstellbar. Dazu ein dynamisches Template fuer alle Erinnerungen.
 *
 * Read-gated by tenant.read; the saves are gated by tenant.manage (the api is the hard line).
 *
 * [Source: apps/web/src/features/administration/pages/RechnungskontrollePage.tsx] (config GET/PUT + form pattern)
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PROCESS_REMINDER_TEMPLATE_PLACEHOLDERS,
  type ProcessReminderTypeConfigDto,
} from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useProcessReminderConfig } from '../hooks';
import { Button, LoadingState, ErrorState, TextInput } from '../components';

const meta = ADMIN_PAGE_META['prozess-erinnerungen'];

/** One editable process-type row (its own local form + Save). */
function ProcessTypeRow({ config }: { config: ProcessReminderTypeConfigDto }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(config.enabled);
  const [l1d, setL1d] = useState(String(config.level_1_days));
  const [l2d, setL2d] = useState(String(config.level_2_days));
  const [l3d, setL3d] = useState(String(config.level_3_days));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const level_1_days = Number(l1d);
      const level_2_days = Number(l2d);
      const level_3_days = Number(l3d);
      if (![level_1_days, level_2_days, level_3_days].every((n) => Number.isInteger(n) && n >= 1)) {
        throw new Error('Bitte ganze Zahlen ab 1 Tag eingeben.');
      }
      if (!(level_1_days <= level_2_days && level_2_days <= level_3_days)) {
        throw new Error('Die Stufen muessen aufsteigend sein: Stufe 1 <= Stufe 2 <= Stufe 3.');
      }
      // The per-level channel toggle is retired: the L1/L2 channel is portal-else-email at send time.
      return apiClient.admin.setProcessReminderType({
        process_typ: config.process_typ,
        enabled,
        level_1_days,
        level_2_days,
        level_3_days,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminProcessReminderConfig() });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div
      data-testid={`prtype-${config.process_typ}`}
      className="rounded-xl border border-border bg-card p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{config.process_label}</div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            aria-label={`Erinnerungen aktiv fuer ${config.process_label}`}
          />
          Aktiv
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">1. Erinnerung</div>
          <TextInput
            inputMode="numeric"
            value={l1d}
            disabled={!enabled}
            onChange={(e) => setL1d(e.target.value)}
            aria-label={`Stufe 1 Frist (Tage) fuer ${config.process_label}`}
            className="tabular-nums"
          />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">2. Erinnerung</div>
          <TextInput
            inputMode="numeric"
            value={l2d}
            disabled={!enabled}
            onChange={(e) => setL2d(e.target.value)}
            aria-label={`Stufe 2 Frist (Tage) fuer ${config.process_label}`}
            className="tabular-nums"
          />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">3. Stufe: Aufgabe an Kundenberater</div>
          <TextInput
            inputMode="numeric"
            value={l3d}
            disabled={!enabled}
            onChange={(e) => setL3d(e.target.value)}
            aria-label={`Stufe 3 Frist (Tage) fuer ${config.process_label}`}
            className="tabular-nums"
          />
        </div>
      </div>

      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}

      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

/** The dynamic reminder template editor (subject + body). */
function TemplateEditor({ subject: initialSubject, body: initialBody }: { subject: string; body: string }) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      if (subject.trim().length === 0 || body.trim().length === 0) {
        throw new Error('Betreff und Text duerfen nicht leer sein.');
      }
      return apiClient.admin.setProcessReminderTemplate({ subject, body });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminProcessReminderConfig() });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-1 text-sm font-semibold">Erinnerungs-Vorlage (Template)</div>
      <p className="mb-3 text-xs text-muted-foreground">
        Wird fuer jede Erinnerung automatisch ausgefuellt. Platzhalter:{' '}
        {PROCESS_REMINDER_TEMPLATE_PLACEHOLDERS.map((p) => `{{${p}}}`).join(', ')}.
      </p>

      <label className="mb-1 block text-xs font-medium text-muted-foreground">Betreff</label>
      <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Vorlage Betreff" />

      <label className="mb-1 mt-3 block text-xs font-medium text-muted-foreground">Text</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        aria-label="Vorlage Text"
        rows={8}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}

      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Vorlage speichern
        </Button>
      </div>
    </div>
  );
}

export function ProzessErinnerungenPage() {
  const { data, isLoading, isError } = useProcessReminderConfig();

  // Re-mount the per-type rows + template editor with a key when the server data changes, so the
  // uncontrolled local form state re-hydrates from the fresh config after a save/invalidation.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (data != null) setVersion((v) => v + 1);
  }, [data]);

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      {isLoading ? (
        <LoadingState />
      ) : isError || data == null ? (
        <ErrorState />
      ) : (
        <div className="space-y-6">
          <TemplateEditor key={`tpl-${version}`} subject={data.template.subject} body={data.template.body} />

          <p
            data-testid="reminder-channel-note"
            className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground"
          >
            Stufe 1 und 2 erinnern den Kunden direkt: Hat der Kunde aktiven Kundenportal-Zugang, wird die
            Erinnerung ins Kundenportal zugestellt, sonst per E-Mail an den Kunden. Stufe 3 erstellt eine
            Aufgabe für den Kundenberater.
          </p>

          <div className="space-y-3">
            {data.process_types.map((cfg) => (
              <ProcessTypeRow key={`${cfg.process_typ}-${version}`} config={cfg} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
